import React, { useState, useEffect } from 'react';
import { X, Moon, Sun, Monitor, RefreshCw, ExternalLink, Palette, Settings, Code, Activity, Check, Star } from 'lucide-react';
import { Button } from './Button';
import { TokenUsageCard } from '../../../features/settings/components/TokenUsageCard';
import { useTokenUsageStore } from '../../store/tokenUsageStore';
import { useCacheStore } from '../../store/cacheStore';
import { ConfirmDialog } from './ConfirmDialog';
import { AccentColorPicker } from './AccentColorPicker';
import { useUIStore } from '../../store/uiStore';
import { useModalRoute } from '@shared/hooks/routing/useModalRoute';
import { useQueryParamSync } from '@shared/hooks/routing/useQueryParamSync';

interface SettingsModalProps {
  currentTheme: string;
  setTheme: (theme: string) => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ currentTheme, setTheme }) => {
  // Use modal routing hook
  const { isOpen, isMounted, close } = useModalRoute('settings');

  // Tab routing via URL (?settingsTab=appearance)
  const { params, setParams } = useQueryParamSync<{ settingsTab?: string }>({
    schema: { settingsTab: 'string' },
    replace: true, // Replace to avoid history pollution on tab changes
  });

  const activeTab = params.settingsTab || 'general';
  const [developerMode, setDeveloperMode] = useState(false);
  const [skipAuth, setSkipAuth] = useState(false);
  const [forceDesktopView, setForceDesktopView] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Token usage and cache stores
  const { breakdown, fetchBreakdown } = useTokenUsageStore();
  const { refreshCache, syncConfirmOpen, setSyncConfirmOpen, lastRefresh, isRefreshing, progress, message, error, clearError } = useCacheStore();

  // Load developer settings on mount
  useEffect(() => {
    if (isOpen) {
      // Dynamically import to avoid circular dependencies
      import('@shared/utils/developerSettings').then(({ DeveloperSettings }) => {
        const settings = DeveloperSettings.getAllSettings();
        setDeveloperMode(settings.enabled);
        setSkipAuth(settings.skipAuth);
        setForceDesktopView(settings.forceDesktopView);
      });
    }
  }, [isOpen]);

  const handleDeveloperModeToggle = (enabled: boolean) => {
    import('@shared/utils/developerSettings').then(({ DeveloperSettings }) => {
      DeveloperSettings.setDeveloperMode(enabled);
      setDeveloperMode(enabled);

      if (!enabled) {
        setSkipAuth(false);
        setForceDesktopView(false);
      }
    });
  };

  const handleSkipAuthToggle = (skip: boolean) => {
    import('@shared/utils/developerSettings').then(({ DeveloperSettings }) => {
      DeveloperSettings.setSkipAuth(skip);
      setSkipAuth(skip);
    });
  };

  const handleForceDesktopViewToggle = (force: boolean) => {
    import('@shared/utils/developerSettings').then(({ DeveloperSettings }) => {
      DeveloperSettings.setForceDesktopView(force);
      setForceDesktopView(force);
    });
  };

  const handleBreakdownToggle = () => {
    const newShowBreakdown = !showBreakdown;
    setShowBreakdown(newShowBreakdown);

    // Fetch breakdown when expanding (if not already loaded)
    if (newShowBreakdown && !breakdown) {
      fetchBreakdown(24);
    }
  };

  const handleSyncCache = async () => {
    const startTime = Date.now();
    console.log('[SettingsModal] Sync Now button clicked', {
      timestamp: new Date().toISOString(),
      lastRefresh,
    });

    try {
      await refreshCache({ confirmed: true });
      const duration = Date.now() - startTime;
      console.log('[SettingsModal] Cache sync completed successfully', {
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      // Enhanced traceback logging
      console.error('[SettingsModal] Sync Cache FAILED:', {
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      });
      console.error('[SettingsModal] Error details:', error);
      if (error instanceof Error) {
        console.error('[SettingsModal] Error message:', error.message);
        console.error('[SettingsModal] Stack trace:', error.stack);
      }
      // Log the full error object for inspection
      try {
        console.error('[SettingsModal] Full error JSON:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      } catch {
        // If error can't be stringified, just log it directly
        console.error('[SettingsModal] Error (non-serializable):', error);
      }
    }
  };

  // Format last sync time as actual timestamp
  const formatLastSync = (lastRefreshStr: string | null) => {
    if (!lastRefreshStr) return 'Never';

    try {
      const lastRefreshDate = new Date(lastRefreshStr);

      // Check if date is valid
      if (isNaN(lastRefreshDate.getTime())) return 'Unknown';

      // Format as readable timestamp: "Dec 16, 2024 at 11:31 AM"
      return lastRefreshDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) + ' at ' + lastRefreshDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Unknown';
    }
  };

  if (!isOpen) return null;

  const themes = [
    { id: 'theme-default', name: 'Default', icon: <Monitor size={16} />, desc: 'Dark Slate with Cyan accents' },
    { id: 'theme-dark', name: 'Dark Mode', icon: <Moon size={16} />, desc: 'True Black / High Contrast' },
    { id: 'theme-light', name: 'Light Mode', icon: <Sun size={16} />, desc: 'Professional Light (WCAG AAA)' },
    { id: 'theme-dynamics', name: 'Dynamics', icon: <Palette size={16} />, desc: 'Microsoft Dynamics with custom accent' },
  ];

  const tabs = [
    { id: 'general', label: 'General', icon: <Settings size={16} /> },
    { id: 'appearance', label: 'Appearance', icon: <Palette size={16} /> },
    { id: 'developer', label: 'Developer', icon: <Code size={16} /> },
    { id: 'usage', label: 'API Usage', icon: <Activity size={16} /> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-4">
      <div className="w-full h-full md:w-[600px] md:h-auto bg-dark-surface border-0 md:border border-dark-border/30 rounded-none md:rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col" style={{ opacity: isMounted ? 1 : 0 }}>
        {/* Header */}
        <div className="bg-dark-header border-b border-dark-border/30 p-3 flex justify-between items-center">
          <h3 className="text-sm font-bold text-white">Settings</h3>
          <button
            onClick={close}
            className="text-dark-muted hover:text-dark-text"
            data-testid='settings-close-btn'
            data-ux='close'
            data-interaction-id='settings-close-btn'><X size={16}/></button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-dark-border/30 bg-dark-bg">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setParams({ settingsTab: tab.id })}
              className={`flex-1 px-4 py-3 text-xs font-semibold flex items-center justify-center gap-2 transition-all duration-150 border-b-2 ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-500 bg-dark-surface'
                  : 'border-transparent text-dark-muted hover:text-dark-text hover:bg-dark-surface/50'
              }`}
              data-testid={`settings-tab-${tab.id}`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 space-y-6 flex-1 overflow-y-auto">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div>
              <h4 className="text-xs font-bold text-dark-muted uppercase tracking-wider mb-3">General Settings</h4>
              <div className="p-4 bg-dark-bg border border-dark-border/30 rounded text-center text-dark-muted">
                General settings will be added here
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
          <div>
             <h4 className="text-xs font-bold text-dark-muted uppercase tracking-wider mb-3">Appearance</h4>
             <div className="space-y-2">
                {themes.map(t => {
                  const uiState = useUIStore.getState();
                  const isDefault = uiState.defaultTheme === t.id;
                  const isSelected = currentTheme === t.id;

                  return (
                    <div
                      key={t.id}
                      className={`w-full flex items-center p-3 rounded border transition-all ${
                        isSelected
                          ? 'bg-brand-600/10 border-brand-500 text-brand-600 ring-1 ring-brand-500'
                          : 'bg-dark-bg border-dark-border/30 text-dark-text hover:bg-dark-surface/50'
                      }`}
                    >
                      <button
                        onClick={() => setTheme(t.id)}
                        className="flex items-center flex-1"
                        data-testid={`theme-${t.id}-btn`}
                        data-ux='action'>
                        <div className={`p-2 rounded-full mr-3 ${isSelected ? 'bg-brand-500 text-white' : 'bg-dark-surface text-dark-muted'}`}>
                          {t.icon}
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-bold flex items-center gap-2">
                            {t.name}
                            {isDefault && (
                              <span className="text-xs bg-amber-900/30 border border-amber-700 text-amber-500 px-1.5 py-0.5 rounded flex items-center gap-1">
                                <Star size={10} className="fill-amber-500" />
                                Default
                              </span>
                            )}
                          </div>
                          <div className="text-xs opacity-70">{t.desc}</div>
                        </div>
                      </button>
                      <div className="flex items-center gap-2 ml-2">
                        {isSelected && !isDefault && (
                          <button
                            onClick={() => useUIStore.getState().setDefaultTheme(t.id as 'theme-default' | 'theme-dark' | 'theme-light' | 'theme-dynamics')}
                            className="text-xs px-2 py-1 bg-dark-surface border border-dark-border/30 text-dark-muted rounded hover:bg-dark-surface/50 hover:text-dark-text transition-all duration-150"
                            title="Set as default theme for new sessions"
                            data-testid={`set-default-${t.id}-btn`}
                            data-ux='action'>
                            Set Default
                          </button>
                        )}
                        {isDefault && isSelected && (
                          <button
                            onClick={() => useUIStore.getState().setDefaultTheme(null)}
                            className="text-xs px-2 py-1 bg-amber-900/20 border border-amber-700 text-amber-500 rounded hover:bg-amber-900/30 transition-all duration-150"
                            title="Remove as default theme"
                            data-testid={`clear-default-${t.id}-btn`}
                            data-ux='action'>
                            <Check size={12} />
                          </button>
                        )}
                        {isSelected && <div className="w-2 h-2 rounded-full bg-brand-500" />}
                      </div>
                    </div>
                  );
                })}
             </div>

             {/* Default Theme Info */}
             <div className="mt-4 p-3 bg-dark-bg border border-dark-border/30 rounded text-xs text-dark-muted">
               <div className="flex items-center gap-2 mb-1">
                 <Star size={12} className="text-amber-500" />
                 <span className="font-semibold text-white">Default Theme</span>
               </div>
               <p>
                 Your default theme is synced to the cloud and will be applied automatically when you log in on a new device or browser.
               </p>
             </div>

             {/* Accent Color Picker (only visible when Dynamics theme selected) */}
             {currentTheme === 'theme-dynamics' && (
               <div className="mt-4 p-4 bg-dark-bg border border-dark-border/30 rounded">
                 <h4 className="text-sm font-bold text-white mb-3">Accent Color</h4>
                 <AccentColorPicker
                   initialHue={useUIStore.getState().accentColor.h}
                   initialSaturation={useUIStore.getState().accentColor.s}
                   initialLightness={useUIStore.getState().accentColor.l}
                   onColorChange={(h, s, l) => {
                     useUIStore.getState().setAccentColor(h, s, l);
                   }}
                 />
               </div>
             )}
          </div>
          )}

          {/* Developer Tab */}
          {activeTab === 'developer' && (
          <div>
            <h4 className="text-xs font-bold text-dark-muted uppercase tracking-wider mb-3">Developer Settings</h4>
            <div className="space-y-3">
              {/* Developer Mode Toggle */}
              <div className="flex items-center justify-between p-3 bg-dark-bg border border-dark-border/30 rounded">
                <div className="flex-1">
                  <div className="text-sm font-bold text-white">Developer Mode</div>
                  <div className="text-xs text-dark-muted mt-0.5">
                    Enable developer-only features for faster development
                  </div>
                </div>
                <button
                  onClick={() => handleDeveloperModeToggle(!developerMode)}
                  className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-dark-bg ${
                    developerMode ? 'bg-brand-600' : 'bg-dark-border'
                  }`}
                  data-testid='unknown-action-btn'
                  data-ux='action'
                  data-interaction-id='unknown-action-btn'>
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-dark-surface transition-transform ${
                      developerMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Skip Authentication Toggle (only visible if dev mode enabled) */}
              {developerMode && (
                <div className="flex items-center justify-between p-3 bg-dark-bg border border-yellow-900/30 rounded">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-yellow-500 flex items-center gap-2">
                      <span className="text-xs bg-yellow-900/30 border border-yellow-900 text-yellow-500 px-1.5 py-0.5 rounded font-mono">DEV</span>
                      Skip Authentication
                    </div>
                    <div className="text-xs text-dark-muted mt-0.5">
                      Bypass landing page and login screen (requires page reload)
                    </div>
                  </div>
                  <button
                    onClick={() => handleSkipAuthToggle(!skipAuth)}
                    className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-dark-bg ${
                      skipAuth ? 'bg-yellow-600' : 'bg-dark-border'
                    }`}
                    data-testid='unknown-action-btn'
                    data-ux='action'
                    data-interaction-id='unknown-action-btn'>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-dark-surface transition-transform ${
                        skipAuth ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Force Desktop View Toggle (only visible if dev mode enabled) */}
              {developerMode && (
                <div className="flex items-center justify-between p-3 bg-dark-bg border border-purple-900/30 rounded">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-purple-400 flex items-center gap-2">
                      <span className="text-xs bg-purple-900/30 border border-purple-900 text-purple-400 px-1.5 py-0.5 rounded font-mono">DEV</span>
                      Force Desktop View
                    </div>
                    <div className="text-xs text-dark-muted mt-0.5">
                      Always use desktop layout on mobile devices (requires page reload)
                    </div>
                  </div>
                  <button
                    onClick={() => handleForceDesktopViewToggle(!forceDesktopView)}
                    className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-dark-bg ${
                      forceDesktopView ? 'bg-purple-600' : 'bg-dark-border'
                    }`}
                    data-testid='unknown-action-btn'
                    data-ux='action'
                    data-interaction-id='unknown-action-btn'>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-dark-surface transition-transform ${
                        forceDesktopView ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Developer Mode Info */}
              {developerMode && (
                <div className="p-3 bg-blue-900/10 border border-blue-900/30 rounded text-xs text-blue-300">
                  <div className="font-bold mb-1">Console Commands Available:</div>
                  <div className="font-mono space-y-0.5 text-xxs">
                    <div>window.devSettings.enable() - Quick enable dev mode</div>
                    <div>window.devSettings.disable() - Disable dev mode</div>
                    <div>window.devSettings.status() - Show current settings</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* API Usage Tab */}
          {activeTab === 'usage' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-dark-muted uppercase tracking-wider">Pipedrive API Usage</h4>
              <a
                href="https://usi.pipedrive.com/settings/usage-caps/api-usage"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand-500 hover:text-brand-400 flex items-center gap-1 transition-all duration-150"
              >
                <ExternalLink size={12} />
                View in Pipedrive
              </a>
            </div>
            <div className="space-y-3">
              {/* Token Usage Card */}
              <TokenUsageCard />

              {/* Expandable Breakdown */}
              <details
                open={showBreakdown}
                onToggle={handleBreakdownToggle}
                className="group"
              >
                <summary className="cursor-pointer text-sm text-brand-500 hover:text-brand-400 list-none flex items-center gap-2">
                  <span className="transform transition-transform group-open:rotate-90">▶</span>
                  View Detailed Breakdown
                </summary>
                <div className="mt-3 p-4 bg-dark-bg border border-dark-border/30 rounded text-sm">
                  {breakdown ? (
                    <div className="space-y-2">
                      <div className="font-bold text-white mb-3">
                        Usage by Endpoint (Last {breakdown.hours}h)
                      </div>
                      {Object.entries(breakdown.breakdown)
                        .sort(([, a], [, b]) => b - a)
                        .map(([endpoint, tokens]) => (
                          <div key={endpoint} className="flex justify-between items-center py-1.5 border-b border-dark-border/30 last:border-0">
                            <span className="text-dark-muted font-mono text-xs">{endpoint}</span>
                            <span className="text-dark-text font-semibold">{tokens.toLocaleString()}</span>
                          </div>
                        ))}
                      <div className="flex justify-between items-center py-2 mt-2 border-t-2 border-dark-border/30 font-bold text-white">
                        <span>Total</span>
                        <span>{breakdown.total.toLocaleString()} tokens</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-dark-muted text-center py-4">
                      Loading breakdown...
                    </div>
                  )}
                </div>
              </details>

              {/* Sync Cache Section */}
              <div className="p-4 bg-dark-bg border border-dark-border/30 rounded">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-bold text-white text-sm">Sync Cache</div>
                    <div className="text-xs text-dark-muted mt-0.5">
                      Last synced: {formatLastSync(lastRefresh)}
                    </div>
                  </div>
                  <button
                    onClick={() => setSyncConfirmOpen(true)}
                    disabled={isRefreshing}
                    className={`px-4 py-2 rounded-lg transition-all duration-150 min-h-[44px] flex items-center gap-2 shrink-0 ${
                      isRefreshing
                        ? 'bg-dark-border text-dark-muted cursor-not-allowed'
                        : 'bg-brand-600 text-white hover:bg-brand-700'
                    }`}
                    data-testid='settings-sync-btn'
                    data-ux='sync'
                    data-interaction-id='settings-sync-btn'>
                    <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                    {isRefreshing ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>

                {/* Progress Bar */}
                {isRefreshing && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-dark-muted mb-1">
                      <span>{message || 'Starting sync...'}</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-dark-border rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-brand-500 h-2 rounded-full transition-all duration-200 ease-out"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && !isRefreshing && (
                  <div className="mt-3 p-3 bg-red-900/20 border border-red-900/30 rounded flex items-start gap-2">
                    <div className="text-red-400 text-xs flex-1">{error}</div>
                    <button
                      onClick={clearError}
                      className="text-red-400 hover:text-red-300 shrink-0"
                      data-testid='settings-error-dismiss-btn'>
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-dark-bg border-t border-dark-border/30 p-3 flex justify-between items-center shrink-0">
          {developerMode && (
            <div className="text-xs text-yellow-500 flex items-center gap-2">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
              Developer mode active
            </div>
          )}
          <div className="ml-auto">
            <Button
              onClick={close}
              variant="primary"
              data-testid='settings-done-btn'
              data-ux='done'
              data-interaction-id='settings-done-btn'>Done</Button>
          </div>
        </div>
      </div>

      {/* Sync Confirmation Dialog */}
      <ConfirmDialog
        isOpen={syncConfirmOpen}
        title="Sync Pipedrive Cache?"
        message="This will fetch all organizations and contacts from Pipedrive."
        variant="warning"
        confirmLabel="Sync Now"
        onConfirm={handleSyncCache}
        onCancel={() => setSyncConfirmOpen(false)}
        details={
          <div className="space-y-1.5">
            <div className="text-dark-muted">Estimated cost: ~70 tokens (v2)</div>
            <div className="text-amber-400 font-semibold">⚠️ Rate limit: 1 sync per 5 minutes</div>
          </div>
        }
      />
    </div>
  );
};
